import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { Link } from '@xing-com/crate-core-router';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { Promotions } from '@xing-com/crate-jobs-domain-search-components-search-box';
import { useAnimatedPlaceholder } from '@xing-com/crate-jobs-domain-search-search-bar';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { IconAISearch } from '@xing-com/icons';
import {
  mediaConfined,
  mediaSmallOrTiny,
  mediaTopBarVisible,
} from '@xing-com/layout-tokens';
import { InputBar } from '@xing-com/text-field';
import { LightMode } from '@xing-com/theme-mode-switcher';
import { scale080, scale090, spaceM, spaceL, spaceXXL } from '@xing-com/tokens';

type Props = {
  withPromotions?: boolean;
  isSearchAndShuffle?: boolean;
  withTitle: boolean;
};

export const FakeInput: React.FC<Props> = ({
  withPromotions,
  isSearchAndShuffle,
  withTitle,
}) => {
  const { formatMessage } = useIntl();
  const isSmallScreen = useMatchMedia(mediaSmallOrTiny);

  const inputRef = useRef(null);
  const runPlaceholderAnimation = useAnimatedPlaceholder(inputRef);

  useEffect(() => {
    runPlaceholderAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TurnOnLightMode isSearchAndShuffle={isSearchAndShuffle}>
      <Container
        $isSearchAndShuffle={isSearchAndShuffle}
        $withTitle={withTitle}
      >
        <StyledLink
          to={ROUTES.searchAi}
          aria-label={formatMessage({
            id: 'JOBS_SEARCH_AI_ENTRYPOINT_BANNER',
          })}
        >
          <Input
            icon={isSmallScreen ? undefined : IconAISearch}
            size="xlarge"
            variant="plain"
            value=""
            ref={inputRef}
            aria-hidden="true"
            tabIndex={-1}
            data-testid="search-bar-fake-input"
            $isSearchAndShuffle={isSearchAndShuffle}
          />
        </StyledLink>
      </Container>
      {withPromotions ? (
        <Promotions isSearchAndShuffle={isSearchAndShuffle} />
      ) : null}
    </TurnOnLightMode>
  );
};

type TurnOnLightModeProps = {
  isSearchAndShuffle?: boolean;
  children: React.ReactNode;
};

const TurnOnLightMode: React.FC<TurnOnLightModeProps> = ({
  isSearchAndShuffle,
  children,
}) =>
  isSearchAndShuffle ? <>{children}</> : <LightMode>{children}</LightMode>;

const StyledLink = styled(Link)`
  display: block;
  margin: 0 auto;
  padding: 0 ${spaceL};
  max-width: 648px;

  &:hover,
  &:focus {
    text-decoration: none;
  }

  @media ${mediaConfined} {
    padding: 0;
  }
`;

const Container = styled.div<{
  $isSearchAndShuffle?: boolean;
  $withTitle?: boolean;
}>`
  ${({ $withTitle }) =>
    $withTitle === false &&
    css`
      padding-top: ${spaceXXL};
    `};

  ${({ $isSearchAndShuffle }) =>
    $isSearchAndShuffle &&
    css`
      @media ${mediaTopBarVisible} {
        padding-top: ${spaceM};
      }
    `};
`;

const Input = styled(InputBar)<{ $isSearchAndShuffle?: boolean }>`
  & input {
    cursor: pointer;
    transition: border 0.18s ease-in-out;

    &::placeholder {
      font-size: ${scale080};
    }

    @media ${mediaConfined} {
      height: ${({ $isSearchAndShuffle }) =>
        $isSearchAndShuffle ? '60px' : '90px'};
      border-radius: 45px;

      &::placeholder {
        font-size: ${scale090};
      }
    }
  }
`;
